// src/modules/app-views/welcome/pages/Init/QuickAccessModules.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Card, Typography, Tooltip, Switch, Button, Grid, theme } from 'antd';
import { CloseCircleOutlined, ToolOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import Icon from 'components/JarvislyComponents/Icon';
import { isAntdIcon, onProduction } from 'utils/helpers';
import appService from 'services/appService';
import accountService from 'modules/app-views/account/service';
import { filterResourcesByEnvironment } from 'utils/resourceEnvironment';
import FirstSubscriptionModal from './modals/FirstSubscriptionModal';

const { useToken } = theme;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { Title, Text } = Typography;

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// *******************************************************************************
// STYLED COMPONENTS
// *******************************************************************************
const ModuleCard = styled(Card)`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  .ant-card-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px 12px;
    min-height: 120px;
    height: 100%;
  }
`;

const IconWrapper = styled.div`
  font-size: 32px;
  color: ${props => props.primaryColor || '#1890ff'};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModuleTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  color: ${props => props.theme?.colorText || 'inherit'};
  line-height: 1.3;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

const ModuleSubtitle = styled.span`
  font-size: 11px;
  font-weight: 400;
  text-align: center;
  color: ${props => props.textMuted || '#999'};
  line-height: 1.2;
  margin-top: 2px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const QuickAccessModules = ({ onClose }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedNavigationMenu = useZustand('app', 'navigationMenu');
  const storedSubscription = useZustand('auth', 'subscription');
  const storedUser = useZustand('auth', 'user');
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const { token } = useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Get activated resources from subscription, filtered by current environment
  const activatedResources = filterResourcesByEnvironment(storedSubscription?.resources || []);

  // Extract visible modules from navigation menu
  // Filter only navigable items (not group titles, not hidden, not dividers)
  // Also filter by activated resources in subscription
  const visibleModules = useMemo(() => {
    const modules = [];

    if (!storedNavigationMenu?.length) return modules;

    storedNavigationMenu.forEach(group => {
      // Process submenu items (these are the actual modules)
      if (group.submenu?.length) {
        group.submenu.forEach(item => {
          // Skip hidden items, dividers, and items without path
          if (item.hide || item.isDivider || !item.path) return;

          // Skip 'welcome' module (we're already on this page)
          if (item.moduleId === 'welcome') return;

          // Check if module has required resources
          // If requiredResources is empty, module is always visible
          // If requiredResources has values, check if at least one is activated
          const hasRequiredResource = hasActivatedResource(
            item.requiredResources,
            activatedResources
          );
          if (!hasRequiredResource) return;

          modules.push({
            key: item.moduleId || item.path,
            path: item.path,
            title: item.title,
            icon: item.icon,
            moduleId: item.moduleId,
            groupKey: group.moduleId || group.path,
            groupTitle: group.title,
          });
        });
      }

      // Also include top-level items that are not group titles
      if (!group.isGroupTitle && !group.hide && group.path && group.moduleId) {
        // Skip 'welcome' module (we're already on this page)
        if (group.moduleId === 'welcome') return;

        // Account module is always visible (user settings, not a paid resource)
        const isAccountModule = group.moduleId === 'account';

        // Check required resources (skip for account module)
        if (!isAccountModule) {
          const hasRequiredResource = hasActivatedResource(
            group.requiredResources,
            activatedResources
          );
          if (!hasRequiredResource) return;
        }

        modules.push({
          key: group.moduleId || group.path,
          path: group.path,
          title: group.title,
          icon: group.icon,
          moduleId: group.moduleId,
          groupKey: group.groupKey || null,
          groupTitle: group.groupTitle || null,
        });
      }
    });

    return modules;
  }, [storedSubscription, storedNavigationMenu, activatedResources]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [groupByMenu, setGroupByMenu] = useState(false);
  const [showDevModal, setShowDevModal] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────
  // Sync groupByMenu preference from store
  useEffect(() => {
    if (!storedUser?._id || !storedParameters) return;

    const savedValue = appService.getModulePreferences({
      category: 'widgets',
      moduleId: 'welcome',
      key: 'groupByMenu',
    });
    setGroupByMenu(savedValue ?? false);
  }, [storedUser?._id, storedParameters]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleModuleClick = useCallback(
    path => {
      // Account settings requires special handling (needs user ID in path)
      if (path === 'app/account' || path === '/app/account') {
        accountService.navigateToAccountSettings({ navigate });
        return;
      }
      navigate(path);
    },
    [navigate]
  );

  const handleClose = useCallback(async () => {
    await appService.updateModulePreferences({
      category: 'widgets',
      data: { showQuickAccessModules: false },
      moduleId: 'welcome',
    });
    onClose?.();
  }, [onClose]);

  const handleOpenDevModal = useCallback(() => setShowDevModal(true), []);

  const handleGroupByMenuChange = useCallback(async checked => {
    setGroupByMenu(checked);
    await appService.updateModulePreferences({
      category: 'widgets',
      data: { groupByMenu: checked },
      moduleId: 'welcome',
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Dev-only: show "configure system" button only on localhost
  const isLocalhost = !onProduction();

  // Build grouped modules when groupByMenu is active
  const groupedModules = useMemo(() => {
    if (!groupByMenu) return null;

    const groups = {};

    visibleModules.forEach(mod => {
      // Modules without a group (e.g. "Account Settings") go into 'account' group
      const key = mod.groupKey || 'account';
      const title = mod.groupTitle || 'account';

      // Skip 'dashboard' group (Welcome is already on this page)
      if (key === 'dashboard') return;

      if (!groups[key]) {
        groups[key] = { key, title, modules: [] };
      }
      groups[key].modules.push(mod);
    });

    // Return as array, preserving the original menu order
    return Object.values(groups);
  }, [groupByMenu, visibleModules]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!visibleModules.length) return null;

  return (
    <>
    <motion.div
      id="tour-quick-access"
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={{ duration: 0.3 }}
    >
      {/* Header with title, switch, and close button */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'center' : 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={3} style={{ margin: 0, padding: '0 4px' }}>
          {translateX('quick_access')}
        </Title>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: isMobile ? 26 : 0,
          marginBottom: isMobile ? 26 : 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              {translateX('segregate_modules')}
            </Text>
            <Switch size="small" checked={groupByMenu} onChange={handleGroupByMenuChange} />
          </div>
        </div>
      </div>

      {/* Modules Grid - flat or grouped */}
      {groupByMenu && groupedModules ? (
        // Grouped view
        groupedModules.map(group => (
          <div key={group.key} style={{ marginBottom: 24 }}>
            <Text
              type="secondary"
              strong
              style={{
                fontSize: 14,
                display: 'block',
                marginBottom: 12,
                padding: '0 4px',
              }}
            >
              {translateX(group.title)}
            </Text>
            <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
              {group.modules.map(module => (
                <Col
                  key={module.key}
                  xs={12}
                  sm={8}
                  md={6}
                  lg={4}
                  xl={4}
                  style={{ display: 'flex' }}
                >
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    style={{ width: '100%', display: 'flex' }}
                  >
                    <ModuleCard onClick={() => handleModuleClick(module.path)}>
                      <IconWrapper primaryColor={token.colorPrimary}>
                        {module.icon && (
                          <Icon
                            type={module.icon}
                            className={!isAntdIcon(module.icon) ? 'react-icon-large' : ''}
                          />
                        )}
                      </IconWrapper>
                      <ModuleTitle>{translateX(module.title)}</ModuleTitle>
                    </ModuleCard>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </div>
        ))
      ) : (
        // Flat view (original)
        <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
          {visibleModules.map(module => (
            <Col key={module.key} xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: 'flex' }}>
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{ width: '100%', display: 'flex' }}
              >
                <ModuleCard onClick={() => handleModuleClick(module.path)}>
                  <IconWrapper primaryColor={token.colorPrimary}>
                    {module.icon && (
                      <Icon
                        type={module.icon}
                        className={!isAntdIcon(module.icon) ? 'react-icon-large' : ''}
                      />
                    )}
                  </IconWrapper>
                  <ModuleTitle>{translateX(module.title)}</ModuleTitle>
                  {module.groupTitle && (
                    <ModuleSubtitle textMuted={token.colorTextSecondary}>
                      {translateX(module.groupTitle)}
                    </ModuleSubtitle>
                  )}
                </ModuleCard>
              </motion.div>
            </Col>
          ))}
        </Row>
      )}
    </motion.div>

    {/* Dev-only: configure system modal */}
    {isLocalhost && (
      <FirstSubscriptionModal show={showDevModal} setShow={setShowDevModal} devMode />
    )}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default QuickAccessModules;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Check if at least one required resource is activated in subscription
 * @param {Array} requiredResources - Array of required resource UIDs
 * @param {Array} activatedResources - Array of activated resource UIDs from subscription
 * @returns {boolean} - True if module should be visible
 */
function hasActivatedResource(requiredResources, activatedResources) {
  // If no required resources, module is always visible
  if (!requiredResources?.length) return true;

  // Check if at least one required resource is activated
  return requiredResources.some(resource => activatedResources.includes(resource));
}
